import classNames from "classnames";
import { get } from "lodash";
import { useRouter } from "next/router";
import React from "react";
import Modal from "../../components/common/Modal";
import useMedia, { MediaSizes } from "../../hooks/useMedia";
import { getFormattedDate } from "../../utils";
import Store from "../../utils/Store";
import constants from "../../utils/constants";
import Toast from "../Toast";
import Button from "../common/Button";
import Checkbox from "../common/Checkbox";
import PrivacyPolicyContent from "./PrivacyPolicyContent";
import TermsAndConditionsContentStatic from "./TermsAndConditionsContent";
import {
  CONFIRMATION_CONTENT,
  LINKS,
  PRIVACY_POLICY,
  TERMS_AND_CONDITIONS,
} from "./constants";
import useAgreementService from "./services";
import AwesomeIcon from "../AwesomeIcon";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "@qure/qure-auth";
import Link from "next/link";

type ModalProps = {
  closeButton?: JSX.Element;
  onClose?: () => void;
  closeOnClickAway?: boolean;
  mode: "readonly" | "interactive";
  showModal?: boolean;
  onAgree?: () => void;
};

const getDefaultProps = (props: ModalProps): Required<ModalProps> => {
  const dupeProps = { ...props };

  if (!dupeProps.closeButton) {
    dupeProps.closeButton = props.mode === "readonly" ? undefined : <></>;
  }

  if (dupeProps.closeOnClickAway == null) {
    dupeProps.closeOnClickAway = false;
  }

  return dupeProps as Required<ModalProps>;
};

type ContentProps = {
  mode: ModalProps["mode"];
  onDisagree: () => void;
  onAgree: () => void;
};

const TermsAndConditionsContent = (props: ContentProps) => {
  const { mode, onDisagree, onAgree } = props;
  const isInteractive = mode === "interactive";
  const { t } = useTranslation();
  const { userInfo, refetchProfile } = useAuth();

  // hooks
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [viewedMap, setViewedMap] = React.useState(new Map());
  const termsAndConditionsRef = React.useRef<HTMLDivElement>(null);
  const privacyPolicyRef = React.useRef<HTMLDivElement>(null);
  const [disclaimerStatus, setDisclaimerStatus] = React.useState(false);
  const maxScroll = React.useRef<number>(0);
  const [currentScroll, setCurrentScroll] = React.useState<number>(0);

  // custom hooks
  const agreementQuery = useAgreementService();
  const router = useRouter();
  const isMobile = useMedia([MediaSizes.MOBILE], [true], false);

  const sectionsRead = Array.from(viewedMap?.values() || []).every((value) => value);
  const passedCriteria = sectionsRead && disclaimerStatus;
  const agreement = get(userInfo, ["agreement"]);
  const agreementDatetime = agreement?.accepted_time ?? "";

  const contents = [
    {
      id: "terms_and_conditions",
      ref: termsAndConditionsRef,
      component: TermsAndConditionsContentStatic,
    },
    {
      id: "privacy_policy",
      ref: privacyPolicyRef,
      component: PrivacyPolicyContent,
    },
  ];

  async function submitAgreement() {
    try {
      const version = constants.TERMS_OF_USE_VERSION;
      await agreementQuery.mutateAsync(version);
      await refetchProfile();
      // router.replace("/patients");
      onAgree();
    } catch (e: any) {
      Toast.error("Something went wrong. Please try again");
    }
  }

  function handleDisagree() {
    onDisagree();
  }

  function handleScroll(e: any) {
    const { target } = e;
    const { clientHeight, scrollTop } = target;

    if (!termsAndConditionsRef.current || !privacyPolicyRef.current) {
      return;
    }

    const termsAndConditionsNodeHeight = termsAndConditionsRef.current.clientHeight;
    const privacyPolicyNodeHeight = privacyPolicyRef.current.clientHeight;

    if (!maxScroll.current) {
      maxScroll.current =
        termsAndConditionsNodeHeight + privacyPolicyNodeHeight - clientHeight;
    }

    if (!currentScroll || currentScroll < scrollTop) {
      setCurrentScroll(scrollTop);
    }

    if (termsAndConditionsNodeHeight == null || privacyPolicyNodeHeight == null) {
      return;
    }

    const isTermsAndConditionsViewed = viewedMap.get(termsAndConditionsRef.current.id);
    const isPrivacyPolicyViewed = viewedMap.get(privacyPolicyRef.current.id);

    let hasChanged = false;

    if (scrollTop <= termsAndConditionsNodeHeight - clientHeight) {
      setActiveIndex(0);
    } else {
      setActiveIndex(1);
    }

    if (
      scrollTop >= termsAndConditionsNodeHeight - clientHeight &&
      !isTermsAndConditionsViewed &&
      scrollTop < termsAndConditionsNodeHeight + privacyPolicyNodeHeight - clientHeight
    ) {
      hasChanged = true;
      viewedMap.set(termsAndConditionsRef.current.id, true);
    }

    if (
      scrollTop >=
        termsAndConditionsNodeHeight + privacyPolicyNodeHeight - clientHeight &&
      !isPrivacyPolicyViewed
    ) {
      hasChanged = true;
      viewedMap.set(privacyPolicyRef.current.id, true);
    }

    if (hasChanged) {
      setViewedMap(new Map(viewedMap));
    }
  }

  React.useEffect(() => {
    const map = new Map();
    LINKS.forEach((link) => {
      map.set(link.id, false);
    });
    setViewedMap(map);
  }, []);

  return (
    <div className="py-4">
      {/* Acceptance */}
      <div
        className={classNames("grid px-6 py-4", {
          // "grid-cols-[214px_564px]": !isMobile,
          "pl-6 grid-cols-1": isMobile,
        })}
      >
        {mode === "readonly" && (
          <React.Fragment>
            <p>{""}</p>
            <div>
              {agreementDatetime !== "" && (
                <div className="my-3">
                  <p className="text-left">
                    ACCEPTED ON {getFormattedDate(agreementDatetime)}
                  </p>
                </div>
              )}
            </div>
          </React.Fragment>
        )}

        {/* main content */}
        <div>
          <div className="terms-of-use bg-gray-100 dark:bg-gray-900 rounded-md border border-gray-600 relative overflow-hidden">
            <div className="pb-4 h-[360px] sm:h-[453px]">
              <div
                className="pl-5 pr-3 scrollbar-light dark:scrollbar h-full overflow-y-scroll"
                onScroll={handleScroll}
              >
                <div className="text-gray-900 dark:text-gray-100 text-left whitespace-pre-wrap">
                  {contents.map((content) => {
                    return (
                      <div
                        ref={content.ref}
                        key={content.id}
                        id={content.id}
                        className="py-6"
                      >
                        {content.component()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* {isInteractive && (
              <progress
                className="w-full h-2 bg-gray-600 rounded-md block sticky bottom-0"
                max={maxScroll.current}
                value={currentScroll}
              />
            )} */}
          </div>
          {/* Checkbox */}
          {isInteractive && (
            <div className="flex gap-2 items-start mt-6">
              <Checkbox
                name="disclaimer"
                id="disclaimer"
                checked={disclaimerStatus}
                onChange={(value) => {
                  setDisclaimerStatus(value);
                  if (value && !sectionsRead) {
                    viewedMap.set("terms_and_conditions", true);
                    viewedMap.set("privacy_policy", true);
                    setViewedMap(new Map(viewedMap));
                  }
                }}
              />
              <label
                htmlFor="disclaimer"
                className="text-md dark:text-gray-200 text-gray-700 text-left cursor-pointer"
              >
                I confirm that I have read and understood the{" "}
                <Link
                  href={`#${TERMS_AND_CONDITIONS}`}
                  className="text-teal-500 dark:text-teal-300"
                >
                  Terms and Conditions
                </Link>{" "}
                and the{" "}
                <Link
                  href={`#${PRIVACY_POLICY}`}
                  className="text-teal-500 dark:text-teal-300"
                >
                  Privacy Policy
                </Link>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {isInteractive && (
        <div className="border-t border-gray-600">
          <ul className="mr-6 gap-5 pt-4 list-none flex">
            <li className="ml-auto">
              <Button
                className="w-[156px]"
                onClick={handleDisagree}
                dataTestId="disagree-button"
              >
                Disagree
              </Button>
            </li>
            <li>
              <Button
                className="w-[156px]"
                isPrimary
                isLoading={agreementQuery.isLoading}
                disabled={!passedCriteria}
                onClick={submitAgreement}
                dataTestId="agree-button"
              >
                Agree
              </Button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

type ConfirmationModalProps = {
  setShowConfirmationModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const ConfirmationModal = (props: ConfirmationModalProps) => {
  const { setShowConfirmationModal } = props;

  function onProceed() {
    Store.update((s) => {
      s.isLogoutOpen = true;
    });
  }

  function onCancel() {
    setShowConfirmationModal(false);
  }

  return (
    <Modal
      className="sm:!w-6/12 lg:!w-5/12"
      show={true}
      closeOnClickAway={false}
      onClose={() => {
        setShowConfirmationModal(false);
      }}
      title="Rejecting license agreement"
    >
      <div>
        <p className="text-left px-6 py-4">{CONFIRMATION_CONTENT}</p>
        <ul className="list-none flex gap-5 mr-6 mb-5">
          <li className="ml-auto">
            <Button className="min-w-[156px]" onClick={onProceed}>
              Proceed
            </Button>
          </li>
          <li>
            <Button className="min-w-[156px]" isPrimary onClick={onCancel}>
              Cancel
            </Button>
          </li>
        </ul>
      </div>
    </Modal>
  );
};

const TermsAndConditionsModal = (props: ModalProps) => {
  const { closeButton, closeOnClickAway, mode, onClose, showModal, onAgree } =
    getDefaultProps(props);
  const { t } = useTranslation();

  const [showConfirmationModal, setShowConfirmationModal] = React.useState(false);

  return (
    <>
      <Modal
        className="max-w-[800px]"
        show={showModal}
        closeButton={closeButton}
        closeOnClickAway={closeOnClickAway}
        onClose={onClose}
        title={t("settings.license-agreement")}
      >
        <TermsAndConditionsContent
          onDisagree={() => {
            setShowConfirmationModal(true);
          }}
          mode={mode}
          onAgree={onAgree}
        />
      </Modal>
      {showConfirmationModal && (
        <ConfirmationModal setShowConfirmationModal={setShowConfirmationModal} />
      )}
    </>
  );
};

export default TermsAndConditionsModal;
